// FE-SCRM-055 — Sales CRM Settings page.
//
// Six sections on one scrolled page (no sub-routes):
//   1. Sales playbooks (incl. pin-as-default star).
//   2. Workflow flags (enable_win_loss_interviews + future stubs).
//   3. Import data (FE-SCRM-048 surface).
//   4. Tag library.
//   5. Broker firm autocomplete defaults.
//   6. Sales CRM preferences (per-user).
//
// Sections 1, 2, and 5 ride the existing BrokerSettings JSON blob —
// one fetch, one mutation hook. Section 6 lives in localStorage
// because there's no UserPref BE table in v1.

import { useContext } from "react";

import { AuthContext } from "../../auth/AuthContext";
import { SettingsImportDataSection } from "../import";
import {
  usePatchBrokerSettingsMutation,
  useBrokerSettingsQuery,
} from "../../settings/hooks";

import { BrokerFirmsSection } from "./BrokerFirmsSection";
import { PlaybooksSection } from "./PlaybooksSection";
import { TagLibrarySection } from "./TagLibrarySection";
import { UserPreferencesSection } from "./UserPreferencesSection";
import { WorkflowFlagsSection } from "./WorkflowFlagsSection";


export function SettingsPanel(): JSX.Element {
  const auth = useContext(AuthContext);
  const entityId = auth?.currentEntityId ?? null;

  const settingsQ = useBrokerSettingsQuery(entityId);
  const patchM = usePatchBrokerSettingsMutation(entityId);

  const settings = settingsQ.data ?? null;
  const pinnedPlaybookId = settings?.pinned_default_playbook_id ?? null;

  return (
    <div
      data-testid="sales-crm-panel-settings"
      className="flex flex-col gap-8"
    >
      <PlaybooksSection
        pinnedPlaybookId={pinnedPlaybookId}
        onPin={(id) =>
          patchM.mutate({ pinned_default_playbook_id: id })}
        patchPending={patchM.isPending}
      />

      <WorkflowFlagsSection
        settings={settings}
        onPatch={patchM.mutate}
        isPending={patchM.isPending}
      />

      <SettingsImportDataSection />

      <TagLibrarySection />

      <BrokerFirmsSection
        settings={settings}
        onPatch={patchM.mutate}
        isPending={patchM.isPending}
      />

      <UserPreferencesSection />
    </div>
  );
}
